import { useEffect, useRef, useCallback } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { RealtimeChannel } from '@supabase/supabase-js';
import { logger } from '@/lib/logger';

const HOOK_NAME = 'useGlobalRealtime';

// Per-table debounce times in ms (high-frequency tables get longer debounce)
const TABLE_DEBOUNCE_MS: Record<string, number> = {
  sales: 800,
  stock_movements: 800,
  ingredients: 500,
  daily_productions: 500,
  rodizio_productions: 500,
  product_losses: 500,
  post_production_losses: 500,
  notifications: 300,
  combos: 300,
  recipes: 300,
  menu_items: 300,
  financial_movements: 500,
  referrals: 300,
};

const DEFAULT_DEBOUNCE_MS = 300;

// Table to query key mapping
const TABLE_INVALIDATION_MAP: Record<string, string[]> = {
  sales: ['sales', 'dashboard-data', 'global-kpis', 'dashboard', 'dashboard-kpis'],
  ingredients: ['ingredients', 'stock-overview', 'stock-movements-today', 'recipes', 'menu-items', 'cmv-impact'],
  stock_movements: ['stock-overview', 'stock-movements-today', 'ingredients'],
  recipes: ['recipes', 'menu-items', 'dashboard-data'],
  menu_items: ['menu-items', 'dashboard-data', 'combos'],
  combos: ['combos', 'menu-items'],
  notifications: ['notifications'],
  daily_productions: ['pending-productions', 'all-pending-productions', 'production-history', 'daily_productions'],
  rodizio_productions: ['pending-productions', 'all-pending-productions', 'production-history', 'rodizio_productions'],
  product_losses: ['product-losses', 'losses-analysis', 'dashboard-data'],
  post_production_losses: ['post-production-losses', 'losses-analysis'],
  service_fees: ['service-fees', 'restaurant-costs', 'dashboard-data', 'global-kpis'],
  payment_fees: ['payment-fees', 'restaurant-costs', 'dashboard-data', 'global-kpis'],
  tax_settings: ['taxes', 'restaurant-costs', 'dashboard-data', 'global-kpis'],
  fixed_costs: ['fixed-costs', 'dashboard-data', 'global-kpis'],
  financial_movements: [
    'financial-movements',
    'balance-sheet-payables',
    'dre-payables-start', 'dre-payables-end',
    'dre-fixed-costs-detail',
    'dashboard-data', 'global-kpis',
  ],
  referrals: ['referrals', 'referral-code'],
};

/**
 * Centralized Realtime hook that consolidates all Postgres subscriptions
 * into a single channel per restaurant.
 * 
 * Benefits:
 * - Reduces connection overhead (1 channel vs 5+ individual)
 * - Debounces invalidations to prevent cache thrashing
 * - Smart invalidation based on affected tables
 */
export const useGlobalRealtime = (restaurantId: string | undefined) => {
  const queryClient = useQueryClient();
  const channelRef = useRef<RealtimeChannel | null>(null);
  const debounceTimersRef = useRef<Map<string, NodeJS.Timeout>>(new Map());

  // Debounced invalidation to prevent multiple rapid invalidations
  // Uses per-table debounce and surgical restaurantId-scoped invalidation
  const debouncedInvalidate = useCallback((queryKeys: string[], table: string) => {
    const debounceMs = TABLE_DEBOUNCE_MS[table] || DEFAULT_DEBOUNCE_MS;
    
    queryKeys.forEach((key) => {
      // Clear existing timer for this key
      const existingTimer = debounceTimersRef.current.get(key);
      if (existingTimer) {
        clearTimeout(existingTimer);
      }

      // Set new debounced invalidation
      const timer = setTimeout(() => {
        logger.debug(HOOK_NAME, `Invalidating query: ${key} (debounce: ${debounceMs}ms)`);
        // Surgical: invalidate scoped to active restaurantId
        if (restaurantId) {
          queryClient.invalidateQueries({ queryKey: [key, restaurantId] });
        }
        // Also invalidate root key (for queries without restaurantId like dashboard-data)
        queryClient.invalidateQueries({ queryKey: [key] });
        debounceTimersRef.current.delete(key);
      }, debounceMs);

      debounceTimersRef.current.set(key, timer);
    });
  }, [queryClient, restaurantId]);

  // Handle realtime payload
  const handleRealtimeChange = useCallback((payload: {
    eventType: string;
    table: string;
    new?: Record<string, unknown>;
    old?: Record<string, unknown>;
  }) => {
    const { table, eventType } = payload;
    
    logger.info(HOOK_NAME, `Realtime change detected`, {
      table,
      event: eventType,
    });

    // Get query keys to invalidate based on table
    const keysToInvalidate = TABLE_INVALIDATION_MAP[table] || [];
    
    if (keysToInvalidate.length > 0) {
      debouncedInvalidate(keysToInvalidate, table);
    }
  }, [debouncedInvalidate]);

  useEffect(() => {
    if (!restaurantId) {
      logger.debug(HOOK_NAME, 'No restaurantId, skipping subscription');
      return;
    }

    // Prevent duplicate subscriptions
    if (channelRef.current) {
      logger.debug(HOOK_NAME, 'Channel already exists, skipping');
      return;
    }

    logger.info(HOOK_NAME, 'Creating global realtime channel', { restaurantId });

    // Create a single channel for all tables
    const channel = supabase
      .channel(`global-realtime-${restaurantId}`)
      // Sales changes
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'sales',
          filter: `restaurant_id=eq.${restaurantId}`,
        },
        (payload) => handleRealtimeChange({ ...payload, table: 'sales' })
      )
      // Ingredients changes
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'ingredients',
          filter: `restaurant_id=eq.${restaurantId}`,
        },
        (payload) => handleRealtimeChange({ ...payload, table: 'ingredients' })
      )
      // Stock movements
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'stock_movements',
          filter: `restaurant_id=eq.${restaurantId}`,
        },
        (payload) => handleRealtimeChange({ ...payload, table: 'stock_movements' })
      )
      // Notifications
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'notifications',
          filter: `restaurant_id=eq.${restaurantId}`,
        },
        (payload) => handleRealtimeChange({ ...payload, table: 'notifications' })
      )
      // Daily productions
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'daily_productions',
          filter: `restaurant_id=eq.${restaurantId}`,
        },
        (payload) => handleRealtimeChange({ ...payload, table: 'daily_productions' })
      )
      // Rodizio productions
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'rodizio_productions',
          filter: `restaurant_id=eq.${restaurantId}`,
        },
        (payload) => handleRealtimeChange({ ...payload, table: 'rodizio_productions' })
      )
      // Product losses
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'product_losses',
          filter: `restaurant_id=eq.${restaurantId}`,
        },
        (payload) => handleRealtimeChange({ ...payload, table: 'product_losses' })
      )
      // Service fees (delivery platform rates)
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'service_fees',
          filter: `restaurant_id=eq.${restaurantId}`,
        },
        (payload) => handleRealtimeChange({ ...payload, table: 'service_fees' })
      )
      // Payment fees (card machine rates)
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'payment_fees',
          filter: `restaurant_id=eq.${restaurantId}`,
        },
        (payload) => handleRealtimeChange({ ...payload, table: 'payment_fees' })
      )
      // Fixed costs
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'fixed_costs',
          filter: `restaurant_id=eq.${restaurantId}`,
        },
        (payload) => handleRealtimeChange({ ...payload, table: 'fixed_costs' })
      )
      // Financial movements (Gestão Financeira)
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'financial_movements',
          filter: `restaurant_id=eq.${restaurantId}`,
        },
        (payload) => handleRealtimeChange({ ...payload, table: 'financial_movements' })
      )
      // Referrals (Programa de Indicações)
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'referrals',
          filter: `referrer_restaurant_id=eq.${restaurantId}`,
        },
        (payload) => handleRealtimeChange({ ...payload, table: 'referrals' })
      )
      .subscribe((status) => {
        logger.info(HOOK_NAME, 'Channel subscription status', { status });
      });

    channelRef.current = channel;

    // Cleanup
    return () => {
      logger.info(HOOK_NAME, 'Cleaning up global realtime channel');
      
      // Clear all debounce timers
      debounceTimersRef.current.forEach((timer) => clearTimeout(timer));
      debounceTimersRef.current.clear();

      // Unsubscribe from channel
      if (channelRef.current) {
        supabase.removeChannel(channelRef.current);
        channelRef.current = null;
      }
    };
  }, [restaurantId, handleRealtimeChange]);

  // Return nothing - this hook is side-effect only
  return null;
};
